import type { GovernedCapabilitySurface } from "./surfaces";

export type GovernedToolGroup = "receivables" | "payables" | "email";

export type AgentCapabilityKey =
  | "find_invoices"
  | "find_bills"
  | "find_customer_payments"
  | "find_supplier_payments"
  | "summarize_ap_aging"
  | "summarize_payments_received"
  | "summarize_payments_made"
  | "summarize_sales"
  | "summarize_product_demand_history"
  | "summarize_purchases"
  | "summarize_open_sales_orders"
  | "summarize_ar_aging"
  | "summarize_customer_invoice_settlement_behavior"
  | "summarize_customer_credit_exposure"
  | "summarize_customer_invoice_charges"
  | "summarize_customer_ar_position"
  | "summarize_pipeline"
  | "search_opportunities"
  | "get_opportunity_detail"
  | "search_cost_book_entries"
  | "search_price_book_entries"
  | "get_product_cost_context"
  | "get_supplier_history"
  | "search_note_activity"
  | "search_email_activity"
  | "get_sent_email"
  | "resolve_entity"
  | "company_presence"
  | "get_note_detail"
  | "list_record_documents"
  | "read_record_document"
  | "find_orders"
  | "get_order_detail"
  | "get_invoice_detail"
  | "get_bill_detail"
  | "find_purchase_execution"
  | "get_purchase_receipt_events"
  | "find_inventory_balances"
  | "find_reorder_status"
  | "find_reorder_with_inbound"
  | "find_inventory_pressure"
  | "find_lots"
  | "get_lot_detail"
  | "find_lot_movements"
  | "find_customer_accounts"
  | "find_suppliers"
  | "get_customer_contacts"
  | "get_supplier_contacts"
  | "find_customer_product_evidence"
  | "list_warehouses"
  | "list_representatives"
  | "list_payment_terms"
  | "list_ledger_accounts"
  | "list_tags"
  | "find_tagged_records"
  | "find_products"
  | "list_certification_types"
  | "find_certification_records"
  | "find_certification_subjects"
  | "get_certification_record";

type ReportCapabilityContract = {
  capabilityKey: AgentCapabilityKey;
  semanticVersion: string;
  capabilityClass: "report";
  capabilityKind: "read" | "action";
  surfaces: GovernedCapabilitySurface[];
  datasetKey: string;
  toolGroup?: GovernedToolGroup;
};

type NonReportCapabilityContract = {
  capabilityKey: AgentCapabilityKey;
  semanticVersion: string;
  capabilityClass: "resolution" | "evidence";
  capabilityKind: "read" | "action";
  surfaces: GovernedCapabilitySurface[];
  datasetKey: null;
  toolGroup?: GovernedToolGroup;
};

export type GovernedCapabilityContract = ReportCapabilityContract | NonReportCapabilityContract;

/** Canonical ordered identity roster. Runtime policy is composed on the server. */
export const GOVERNED_CAPABILITY_CONTRACTS = [
  {
    capabilityKey: "find_invoices",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "invoice-finder",
    toolGroup: "receivables",
  },
  {
    capabilityKey: "find_bills",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "bill-finder",
    toolGroup: "payables",
  },
  {
    capabilityKey: "find_customer_payments",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "customer-payment-finder",
    toolGroup: "receivables",
  },
  {
    capabilityKey: "find_supplier_payments",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "supplier-payment-finder",
    toolGroup: "payables",
  },
  {
    capabilityKey: "summarize_ap_aging",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "ap-aging",
    toolGroup: "payables",
  },
  {
    capabilityKey: "summarize_payments_received",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "payments-received",
    toolGroup: "receivables",
  },
  {
    capabilityKey: "summarize_payments_made",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "payments-made",
    toolGroup: "payables",
  },
  {
    capabilityKey: "summarize_sales",
    semanticVersion: "1.4.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "sales-summary",
  },
  {
    capabilityKey: "summarize_product_demand_history",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "product-demand-history",
  },
  {
    capabilityKey: "summarize_purchases",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "purchase-summary",
  },
  {
    capabilityKey: "summarize_open_sales_orders",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "open-sales-orders",
  },
  {
    capabilityKey: "summarize_ar_aging",
    toolGroup: "receivables",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "ar-aging",
  },
  {
    capabilityKey: "summarize_customer_invoice_settlement_behavior",
    toolGroup: "receivables",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "customer-invoice-settlement-behavior",
  },
  {
    capabilityKey: "summarize_customer_credit_exposure",
    toolGroup: "receivables",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "customer-credit-exposure",
  },
  {
    capabilityKey: "summarize_customer_invoice_charges",
    toolGroup: "receivables",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "customer-invoice-charges",
  },
  {
    capabilityKey: "summarize_customer_ar_position",
    toolGroup: "receivables",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "customer-ar-position",
  },
  {
    capabilityKey: "summarize_pipeline",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "pipeline-summary",
  },
  {
    capabilityKey: "search_opportunities",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "opportunities",
  },
  {
    capabilityKey: "get_opportunity_detail",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "opportunity-detail",
  },
  {
    capabilityKey: "search_cost_book_entries",
    semanticVersion: "1.4.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "cost_book_entries",
  },
  {
    capabilityKey: "search_price_book_entries",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "price_book_entries",
  },
  {
    capabilityKey: "get_supplier_history",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "supplier_history",
  },
  {
    capabilityKey: "get_product_cost_context",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "product_cost_context",
  },
  {
    capabilityKey: "search_note_activity",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "note-activity",
  },
  {
    capabilityKey: "search_email_activity",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "email-activity",
    toolGroup: "email",
  },
  {
    capabilityKey: "get_sent_email",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "sent-email",
    toolGroup: "email",
  },
  {
    capabilityKey: "resolve_entity",
    semanticVersion: "1.2.0",
    capabilityClass: "resolution",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: null,
  },
  {
    capabilityKey: "company_presence",
    semanticVersion: "1.0.0",
    capabilityClass: "resolution",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: null,
  },
  {
    capabilityKey: "get_note_detail",
    semanticVersion: "1.1.0",
    capabilityClass: "evidence",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: null,
  },
  {
    capabilityKey: "list_record_documents",
    semanticVersion: "1.2.0",
    capabilityClass: "evidence",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: null,
  },
  {
    capabilityKey: "read_record_document",
    semanticVersion: "1.2.0",
    capabilityClass: "evidence",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: null,
  },
  {
    capabilityKey: "find_orders",
    semanticVersion: "1.4.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "order-finder",
  },
  {
    capabilityKey: "get_order_detail",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "order-detail",
  },
  {
    capabilityKey: "get_invoice_detail",
    toolGroup: "receivables",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "invoice-detail",
  },
  {
    capabilityKey: "get_bill_detail",
    toolGroup: "payables",
    semanticVersion: "1.3.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "bill-detail",
  },
  {
    capabilityKey: "find_purchase_execution",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "purchase-execution",
  },
  {
    capabilityKey: "get_purchase_receipt_events",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "purchase-receipt-events",
  },
  {
    capabilityKey: "find_inventory_balances",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "inventory_balances",
  },
  {
    capabilityKey: "find_reorder_status",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "reorder-status",
  },
  {
    capabilityKey: "find_reorder_with_inbound",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "reorder-with-inbound",
  },
  {
    capabilityKey: "find_inventory_pressure",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    // Assistant only. The calendar reaches the same read through its own tRPC procedure, and no
    // public-API consumer has asked for it — declaring `public_api` would mean a route, an
    // api-key scope and a transport adapter for a surface nobody requested.
    surfaces: ["assistant"],
    datasetKey: "inventory-pressure",
  },
  {
    capabilityKey: "find_lots",
    semanticVersion: "1.3.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "lots",
  },
  {
    capabilityKey: "get_lot_detail",
    semanticVersion: "1.3.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "lot-detail",
  },
  {
    capabilityKey: "find_lot_movements",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["public_api", "assistant"],
    datasetKey: "lot-movements",
  },
  {
    capabilityKey: "find_customer_accounts",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "customer-accounts",
  },
  {
    capabilityKey: "find_suppliers",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "supplier-directory",
  },
  {
    capabilityKey: "get_customer_contacts",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "customer-contacts",
  },
  {
    capabilityKey: "get_supplier_contacts",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "supplier-contacts",
  },
  {
    capabilityKey: "find_customer_product_evidence",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant"],
    datasetKey: "customer-product-evidence",
  },
  {
    capabilityKey: "list_warehouses",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "warehouse-directory",
  },
  {
    capabilityKey: "list_representatives",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "representative-directory",
  },
  {
    capabilityKey: "list_payment_terms",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "payment-terms-directory",
  },
  {
    capabilityKey: "list_ledger_accounts",
    semanticVersion: "1.0.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "ledger-account-directory",
  },
  {
    capabilityKey: "list_tags",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "tag-vocabulary",
  },
  {
    capabilityKey: "find_tagged_records",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "tagged-records",
  },
  {
    capabilityKey: "find_products",
    semanticVersion: "1.6.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "product-directory",
  },
  {
    capabilityKey: "list_certification_types",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "certification-types",
  },
  {
    capabilityKey: "find_certification_records",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "certification-records",
  },
  {
    capabilityKey: "find_certification_subjects",
    semanticVersion: "1.1.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "certification-subjects",
  },
  {
    capabilityKey: "get_certification_record",
    semanticVersion: "1.2.0",
    capabilityClass: "report",
    capabilityKind: "read",
    surfaces: ["assistant", "public_api"],
    datasetKey: "certification-record-detail",
  },
] satisfies GovernedCapabilityContract[];

export type GovernedInsightReportCapabilityKey = Extract<
  (typeof GOVERNED_CAPABILITY_CONTRACTS)[number],
  { capabilityClass: "report" }
>["capabilityKey"];
